import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { adminApi } from "@/lib/api";
import {
  MailCheck, ShieldCheck, CircleCheck, CircleX, Send, Plus, Trash2, RefreshCw, TriangleAlert,
} from "lucide-react";

/** Phase 11.5 — Email mode ladder: SIMULATED → TEST MODE → LIVE.
 * LIVE requires a deliberate typed action AND all production gates green.
 * No provider credentials are ever entered or shown here — environment only. */

const MODE_STYLE = {
  SIMULATED: "bg-slate-100 text-slate-800 border-slate-300",
  TEST: "bg-amber-50 text-amber-900 border-amber-300",
  LIVE: "bg-emerald-50 text-emerald-800 border-emerald-300",
};

const GoLiveDialog = ({ disabled, onDone }) => {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const act = async () => {
    try {
      await adminApi.post("/admin/email/mode-control", { mode: "live", confirm: confirmText });
      toast.success("LIVE mode enabled — production delivery active.");
      setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "LIVE change blocked."); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" disabled={disabled} className="bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="email-golive-btn">
          Go LIVE
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <TriangleAlert className="h-5 w-5 text-amber-600" /> Enable LIVE production delivery
          </DialogTitle>
          <DialogDescription>
            Real email will be sent to real recipients. This requires all production gates green
            and is recorded in the audit log.
          </DialogDescription>
        </DialogHeader>
        <Label className="text-xs">Type <span className="font-mono font-bold">GO LIVE</span> to confirm</Label>
        <Input className="mt-1 font-mono" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
               data-testid="email-golive-confirm-input" />
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white w-full" onClick={act}
                disabled={confirmText !== "GO LIVE"} data-testid="email-golive-confirm-btn">
          Enable LIVE delivery
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default function EmailMode() {
  const [data, setData] = useState(null);
  const [newAddr, setNewAddr] = useState("");
  const [testTo, setTestTo] = useState("");

  const load = useCallback(() => {
    adminApi.get("/admin/email/mode-control").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setMode = async (mode) => {
    try {
      await adminApi.post("/admin/email/mode-control", { mode });
      toast.success(`Mode set to ${mode.toUpperCase()}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mode change blocked."); }
  };

  const toggleGate = async (key, value) => {
    try {
      await adminApi.patch("/admin/email/gates", { [key]: value });
      toast.success("Gate confirmation saved.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };

  const addAddress = async () => {
    if (!newAddr.trim()) return;
    try {
      await adminApi.post("/admin/email/test-addresses", { email: newAddr.trim() });
      toast.success("Test address approved.");
      setNewAddr(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add address."); }
  };

  const removeAddress = async (email) => {
    try {
      await adminApi.delete(`/admin/email/test-addresses/${encodeURIComponent(email)}`);
      toast.success("Test address removed.");
      load();
    } catch (e) { toast.error("Could not remove address."); }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter a recipient."); return; }
    try {
      const r = await adminApi.post("/admin/email/send-test", { to_email: testTo.trim() });
      toast.success(`Test message status: ${r.data.status} (mode ${r.data.mode}).`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Test send failed."); }
  };

  if (!data) return <Skeleton className="h-96" />;
  const eff = data.effective;
  const manualKeys = ["spf_configured", "dkim_configured", "dmarc_checked", "from_verified"];

  return (
    <div data-testid="admin-email-mode-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground inline-flex items-center gap-2">
            <MailCheck className="h-6 w-6 text-accent" /> Email Mode
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            SIMULATED → TEST MODE → LIVE. Credentials live only in the backend environment — never here.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="email-mode-refresh">
          <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Current mode — unmistakable */}
      <Card className={`border-2 ${eff.mode === "SIMULATED" ? "border-slate-300" : eff.mode === "TEST" ? "border-amber-300" : "border-emerald-400"}`}
            data-testid="email-mode-banner">
        <CardContent className="p-6">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="outline" className={`${MODE_STYLE[eff.mode]} text-sm font-bold px-3 py-1`}
                   data-testid="email-mode-current">
              EMAIL MODE: {eff.mode === "TEST" ? "TEST MODE" : eff.mode}
            </Badge>
            <p className="text-sm text-foreground">{eff.description}</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mt-5">
            {data.ladder.map((l) => (
              <div key={l.mode}
                   className={`rounded-lg border p-4 ${eff.mode === l.mode ? "border-primary bg-blue-50/50" : "border-slate-200 bg-white"}`}
                   data-testid={`email-ladder-${l.mode.toLowerCase()}`}>
                <p className="font-heading font-bold text-sm text-foreground">{l.mode === "TEST" ? "TEST MODE" : l.mode}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{l.label.split("— ")[1]}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-5">
            <Button size="sm" variant={eff.mode === "SIMULATED" ? "default" : "outline"}
                    className={eff.mode === "SIMULATED" ? "bg-primary hover:bg-primary/90" : ""}
                    onClick={() => setMode("simulated")} data-testid="email-set-simulated">
              Set SIMULATED
            </Button>
            <Button size="sm" variant="outline" onClick={() => setMode("test")} data-testid="email-set-test">
              Set TEST MODE
            </Button>
            <GoLiveDialog disabled={!data.all_gates_green} onDone={load} />
          </div>
          {!data.all_gates_green && (
            <p className="text-[11px] text-muted-foreground mt-2">
              Go LIVE stays locked until every production gate below is green.
            </p>
          )}
          {data.launch_gate && !data.launch_gate.live_email_allowed && (
            <p className="text-[11px] text-red-700 mt-1" data-testid="email-launch-gate-block">
              Also locked by the Launch Gate — required red: {data.launch_gate.required_red.join(", ")}.
              An automated green test is not a substitute for a real person completing the workflow unaided.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Gates */}
      <Card className="mt-6" data-testid="email-gates-card">
        <CardContent className="p-6">
          <h2 className="font-heading font-bold text-lg text-foreground inline-flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-accent" /> Production gates
            <Badge variant="outline" className={data.all_gates_green
              ? "bg-emerald-50 text-emerald-800 border-emerald-300 text-[10px]"
              : "bg-amber-50 text-amber-900 border-amber-300 text-[10px]"}
              data-testid="email-gates-summary">
              {data.all_gates_green ? "ALL GREEN" : "NOT READY"}
            </Badge>
          </h2>
          <div className="mt-4 space-y-3">
            {Object.entries(data.gates).map(([key, g]) => (
              <div key={key} className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3 last:border-0"
                   data-testid={`email-gate-${key}`}>
                <div className="flex items-start gap-2.5 min-w-0">
                  {g.ok ? <CircleCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                        : <CircleX className="h-5 w-5 text-slate-400 shrink-0 mt-0.5" />}
                  <div>
                    <p className="text-sm font-semibold text-foreground">{g.label}</p>
                    <p className="text-[11px] text-muted-foreground">{g.detail}</p>
                  </div>
                </div>
                {!g.auto && (
                  <Switch checked={g.ok} onCheckedChange={(v) => toggleGate(key, v)}
                          data-testid={`email-gate-switch-${key}`} />
                )}
              </div>
            ))}
          </div>
          <div className="rounded-lg border border-slate-200 bg-secondary p-4 mt-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
              Sender-domain authentication guidance
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed" data-testid="email-sender-guidance">
              {data.sender_guidance}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* TEST MODE delivery matrix — every message type must be proven in TEST before LIVE */}
      {data.delivery_matrix && (
        <Card className="mt-6" data-testid="email-delivery-matrix">
          <CardContent className="p-6">
            <h2 className="font-heading font-bold text-lg text-foreground inline-flex items-center gap-2">
              <Send className="h-5 w-5 text-accent" /> TEST MODE delivery matrix
              <Badge variant="outline"
                     className={data.delivery_matrix.matrix_green
                       ? "bg-emerald-50 text-emerald-800 border-emerald-300 text-[10px]"
                       : "bg-amber-50 text-amber-900 border-amber-300 text-[10px]"}
                     data-testid="email-matrix-summary">
                {data.delivery_matrix.matrix_green ? "COMPLETE" : "INCOMPLETE"}
              </Badge>
            </h2>
            <p className="text-[11px] text-muted-foreground mt-1">
              Every message type must be successfully delivered to an approved test address in TEST MODE —
              and the three behaviors confirmed — before LIVE even becomes available.
            </p>
            <div className="mt-4 space-y-2">
              {data.delivery_matrix.message_types.map((m) => (
                <div key={m.type} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2 last:border-0"
                     data-testid={`email-matrix-${m.type}`}>
                  <div className="flex items-center gap-2.5">
                    {m.ok ? <CircleCheck className="h-4 w-4 text-emerald-600" />
                          : <CircleX className="h-4 w-4 text-slate-400" />}
                    <span className="text-sm text-foreground">{m.label}</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground">
                    {m.test_deliveries} TEST deliver{m.test_deliveries === 1 ? "y" : "ies"}
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-2">
              {data.delivery_matrix.behaviors.map((b) => (
                <div key={b.key} className="flex items-center justify-between gap-3"
                     data-testid={`email-matrix-behavior-${b.key}`}>
                  <div className="flex items-center gap-2.5">
                    {b.ok ? <CircleCheck className="h-4 w-4 text-emerald-600" />
                          : <CircleX className="h-4 w-4 text-slate-400" />}
                    <span className="text-sm text-foreground">{b.label}</span>
                  </div>
                  <Switch checked={b.ok} onCheckedChange={(v) => toggleGate(b.key, v)}
                          data-testid={`email-matrix-switch-${b.key}`} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approved test addresses + test send */}
      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card data-testid="email-test-addresses-card">
          <CardContent className="p-6">
            <h2 className="font-heading font-bold text-base text-foreground">Approved test addresses</h2>
            <p className="text-[11px] text-muted-foreground mt-1">
              In TEST MODE, real delivery is allowed ONLY to these addresses. Everything else is
              suppressed and logged.
            </p>
            <div className="flex gap-2 mt-3">
              <Input value={newAddr} placeholder="tester@example.com" maxLength={200}
                     onChange={(e) => setNewAddr(e.target.value)} data-testid="email-test-address-input" />
              <Button size="sm" variant="outline" onClick={addAddress} data-testid="email-test-address-add">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-3 space-y-1.5">
              {(data.approved_test_addresses || []).length === 0 && (
                <p className="text-xs text-muted-foreground">No approved test addresses yet.</p>
              )}
              {(data.approved_test_addresses || []).map((a) => (
                <div key={a} className="flex items-center justify-between gap-2 rounded border border-slate-200 px-3 py-1.5">
                  <span className="text-xs font-mono text-foreground">{a}</span>
                  <Button size="sm" variant="ghost" className="h-6 px-1.5 text-destructive"
                          onClick={() => removeAddress(a)} data-testid={`email-test-address-remove-${a}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="email-send-test-card">
          <CardContent className="p-6">
            <h2 className="font-heading font-bold text-base text-foreground">Send a delivery test</h2>
            <p className="text-[11px] text-muted-foreground mt-1">
              Goes through the CURRENT mode: simulated log-only, TEST-restricted, or live.
              Two successful deliveries to distinct addresses are required before LIVE.
            </p>
            <div className="flex gap-2 mt-3">
              <Input value={testTo} placeholder="recipient@example.com" maxLength={200}
                     onChange={(e) => setTestTo(e.target.value)} data-testid="email-send-test-input" />
              <Button size="sm" className="bg-primary hover:bg-primary/90" onClick={sendTest}
                      data-testid="email-send-test-btn">
                <Send className="h-4 w-4 mr-1.5" /> Send
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
